import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Flex,
  Heading,
  Text,
  Button,
  Spinner,
  Box,
  Spacer,
  Textarea,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';
import { baseUrl } from '../../utils/utils';
import CustomButton from '../../components/CustomButton';
import { BiArrowBack } from 'react-icons/bi';

// DisplayData component to match the style from CustomerList
const DisplayData = ({ title, value, fs = "14px", v_color = "#333", fw = "normal" }) => (
  <Box mb={2}>
    <Text fontSize={fs} color={v_color} fontWeight={fw}>
      {title && `${title}: `}{value || "NA"}
    </Text>
  </Box>
);

const TicketDetails = () => {
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { state } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const fetchTicketDetails = async () => {
      try {
        const formData = new FormData();
        formData.append("ticketid", state?.ticketid);

        const response = await fetch(baseUrl + 'tickets/get_ticket_details.php', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.status === 'success' && data.data.length > 0) {
          // Store the first item as main ticket info and all items as messages
          setTicketData({
            main: data.data[0],
            messages: data.data
          });
        } else {
          setError('Ticket not found');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        if (err.name === 'TypeError' && err.message.includes('fetch')) {
          setError('Network error - please check your connection and server status');
        } else if (err.message.includes('CORS')) {
          setError('CORS error - please contact administrator');
        } else {
          setError(`Failed to fetch ticket details: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    if (state?.ticketid) {
      fetchTicketDetails();
    } else {
      setError('No ticket ID provided');
      setLoading(false);
    }
  }, [state]);

  const formatToIndianDateTime = (dateString) => {
    if (!dateString) return "NA";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleReplySubmit = async () => {
    if (!replyText.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a reply message',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
  
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      // Use the ID from the first ticket entry (main ticket)
      formData.append("ticketid", ticketData.main.id);  // Changed from ticketid to id
      formData.append("admin_comments", replyText);
  
      const response = await fetch(baseUrl + 'tickets/duplicate_ticket.php', {
        method: 'POST',
        body: formData,
      });
  
      const data = await response.json();
  
      if (data.status === 'success') {
        toast({
          title: 'Success',
          description: 'Reply submitted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
  
        // Create a new message object for the reply
        const newMessage = {
          ...ticketData.main,
          id: Date.now().toString(), // Temporary ID
          admin_comments: replyText,
          comments: null,
          created_at: new Date().toISOString()
        };
  
        // Update the messages array
        setTicketData(prev => ({
          ...prev,
          messages: [...prev.messages, newMessage]
        }));
  
        setReplyText('');
        onClose();
      } else {
        throw new Error(data.message || 'Failed to submit reply');
      }
    } catch (err) {
      console.error('Error submitting reply:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to submit reply',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  if (loading) {
    return (
      <Flex justify="center" align="center" height="50vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Card bg="#fff" paddingInline={5} paddingBlock={4} textAlign="center">
        <Text color="red.500">{error}</Text>
        <Button mt={4} onClick={() => navigate('/support', {
          state: {
            page: state?.page,
            rows: state?.rows,
            initialValue: state?.initialValue
          }
        })}>
          Back to Tickets
        </Button>
      </Card>
    );
  }

  if (!ticketData) {
    return (
      <Card bg="#fff" paddingInline={5} paddingBlock={4} textAlign="center">
        <Text>No ticket data available</Text>
        <Button mt={4} onClick={() =>
          navigate('/support', {
            state: {
              page: state?.page,
              rows: state?.rows,
              initialValue: state?.initialValue
            }
          })
        }>
          Back to Tickets
        </Button>
      </Card>
    );
  }

  return (
    <>
      {/* Header Card */}
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        display="flex"
        flexDirection={["column", "column", "row"]}
        alignItems={["stretch", "stretch", "center"]}
        gap={3}
        mb={4}
      >
        <Heading fontSize="xl">Ticket Details</Heading>
        <Spacer />
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title={"Back to Tickets"}
          height={"30px"}
          b_color={"#000"}
          onClick={() =>
            navigate('/support', {
              state: {
                page: state?.page,
                rows: state?.rows,
                initialValue: state?.initialValue
              }
            })
          }
        />
      </Card>

      <Box p={[2, 4]}>
        <Flex 
          gap={4} 
          align="flex-start"
          direction={["column", "column", "row"]}
        >
          {/* Main Ticket Details Card - Compact */}
          <Box
            flex={["none", "none", "3"]}
            width={["100%", "100%", "auto"]}
            bg="white"
            border="1px solid #e2e8f0"
            borderRadius="md"
            p={[4, 6]}
          >
            <Heading size="md" mb={4}>Ticket #{ticketData.main.ticketid}</Heading>

            <Box mb={4}>
              <DisplayData title="Name" value={ticketData.main.name || "NA"} />
              <DisplayData title="Email" value={ticketData.main.email  || "NA"} />
              <DisplayData title="Mobile" value={ticketData.main.mobile  || "NA"} />
              <DisplayData title="Subject" value={ticketData.main.subject  || "NA"} />
              <DisplayData title="Status" value={ticketData.main.ticketstatus === "1" ? "Resolved" : "Pending"} />
              <DisplayData fs="12px" title="Created At" value={formatToIndianDateTime(ticketData.main.created_at)  || "NA"} />
            </Box>
          </Box>

          {/* Chat Section - WhatsApp Style */}
          <Box
            flex={["none", "none", "1.5"]}
            width={["100%", "100%", "auto"]}
            bg="white"
            border="1px solid #e2e8f0"
            borderRadius="md"
            maxH={["400px", "500px"]}
            display="flex"
            flexDirection="column"
          >
            {/* Chat Header */}
            <Box p={3} borderBottom="1px solid #e2e8f0" bg="gray.50">
              <Heading size="sm" color="gray.700">Conversation</Heading>
            </Box>

            {/* Messages Container */}
            <Box flex="1" overflowY="auto" p={3} bg="#f9f9f9">
              {ticketData.messages.map((msg, index) => (
                <React.Fragment key={index}>
                  {/* Customer Message (on left) */}
                  {msg.comments && (
                    <Flex justify="flex-start" mb={4}>
                      <Box maxW={["85%", "70%"]}>
                        <Text fontSize="xs" color="gray.600" mb={1}>
                          <b>{msg.name || "Customer"}</b> •<br/> {formatToIndianDateTime(msg.created_at)}
                        </Text>
                        <Box
                          bg="gray.100"
                          color="gray.800"
                          px={4}
                          py={2}
                          borderRadius="20px"
                          borderBottomLeftRadius="0"
                          fontSize="sm"
                          border="1px solid #e2e8f0"
                        >
                          {msg.comments}
                        </Box>
                      </Box>
                    </Flex>
                  )}

                  {/* Admin Message (on right) */}
                  {msg.admin_comments && (
                    <Flex justify="flex-end" mb={4}>
                      <Box maxW={["85%", "70%"]} textAlign="right">
                        <Text fontSize="xs" color="gray.600" mb={1}>
                          <b>Support Team</b> •<br/> {formatToIndianDateTime(msg.created_at)}
                        </Text>
                        <Box
                          bg="green.500"
                          color="white"
                          px={4}
                          py={2}
                          borderRadius="20px"
                          borderBottomRightRadius="0"
                          fontSize="sm"
                        >
                          {msg.admin_comments}
                        </Box>
                      </Box>
                    </Flex>
                  )}
                </React.Fragment>
              ))}
            </Box>

            {/* Reply Button */}
            <Box p={3} borderTop="1px solid #e2e8f0" bg="gray.50">
              <Button
                colorScheme="blue"
                size="sm"
                width="full"
                onClick={onOpen}
                leftIcon={<Text fontSize="lg"></Text>}
              >
                Reply
              </Button>
            </Box>
          </Box>
        </Flex>

        {/* Modal for Reply - Simplified */}
        <Modal isOpen={isOpen} onClose={onClose} size={["full", "md"]}>
          <ModalOverlay />
          <ModalContent m={[0, "auto"]} maxW={["100%", "md"]}>
            <ModalHeader fontSize="lg">Reply to Customer</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Textarea
                placeholder="Type your reply here..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                minHeight="100px"
                resize="vertical"
                focusBorderColor="blue.500"
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleReplySubmit}
                isLoading={isSubmitting}
                loadingText="Sending..."
              >
                Send Reply
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </>
  );
};

export default TicketDetails;